import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import { LuShare, LuSquarePlus, LuX } from 'react-icons/lu';

import { useHeadTags, HeadTag } from '~/hooks/useHeadTags';

import styles from './Animator.module.css';

/** Événement `beforeinstallprompt` (Chrome/Edge/Samsung Internet), absent de lib.dom. */
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Capture au niveau module : Chrome émet l'événement dès que le manifest injecté
// est jugé installable, potentiellement avant le mount du bandeau (qui garde son
// propre listener pour les émissions suivantes — Chrome ré-émet après un refus).
let deferredPrompt: BeforeInstallPromptEvent | null = null;
window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // coupe la mini-infobar Chrome : on affiche notre bandeau à la place
    deferredPrompt = e as BeforeInstallPromptEvent;
});

/**
 * Balises PWA de la zone animateur. Posées au montage du layout et retirées en
 * sortant : le manifest est scopé start_url + scope /animator, il ne doit pas
 * rendre installable le reste de la console.
 */
const ANIMATOR_HEAD_TAGS: HeadTag[] = [
    { tag: 'link', attrs: { rel: 'manifest', href: '/animator.webmanifest' } },
    { tag: 'meta', attrs: { name: 'theme-color', content: '#070219' } },
    { tag: 'meta', attrs: { name: 'apple-mobile-web-app-capable', content: 'yes' } },
    { tag: 'meta', attrs: { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' } },
    { tag: 'meta', attrs: { name: 'apple-mobile-web-app-title', content: 'Unboared' } },
    { tag: 'link', attrs: { rel: 'apple-touch-icon', href: '/assets/images/animator-icon-180.png' } },
];

const IS_IOS =
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS ≥ 13 se présente comme un Mac dans le user agent ; le tactile fait la différence.
    (/macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/** Lancée depuis l'écran d'accueil (PWA déjà installée) : plus rien à proposer. */
function isStandalone() {
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
}

/**
 * Layout des routes /animator (cf MainRoutes) : monte une seule fois, pour toute
 * la zone animateur, les balises PWA (le manifest, scopé start_url + scope
 * /animator, ne doit pas rendre installable le reste de la console) et
 * l'enregistrement du service worker. Une future page animator héritera
 * automatiquement des deux via l'Outlet. Le SW associé ne cache aucun asset de
 * l'app — voir public/animator-sw.js.
 *
 * Les balises passaient par <Helmet> jusqu'au 10/09/2026 — et n'arrivaient donc
 * jamais dans le <head> : react-helmet est inerte dans cette app, l'install PWA
 * animateur était cassée sur Android/Chrome (pas de manifest = pas de
 * `beforeinstallprompt` = bandeau « Installer l'app » jamais proposé). Cf gotcha
 * #154 et `useHeadTags`.
 *
 * Le layout coupe aussi la traduction automatique du navigateur sur toute la zone.
 * Le traducteur de Chrome emballe chaque nœud texte dans un <font> ; React ne
 * retrouve plus les nœuds qu'il a posés et plante au premier bouton qui change de
 * libellé — et le cockpit en est plein (Lancer ↔ Stopper, Afficher ↔ Masquer le QR,
 * Pause ↔ Reprendre). Crash SERGINO ANIMATION du 10/09/2026, cf gotcha #153.
 *
 * Depuis la traduction du cockpit (7 langues), la cause d'origine — un `<html lang>`
 * qui mentait sur une zone écrite en dur en français — a disparu : la zone parle la
 * langue de l'établissement. Le marqueur reste, pour la raison qui lui survit : le
 * cockpit est DÉJÀ dans la bonne langue, une traduction navigateur ne lui apporte
 * rien et ne peut que rejouer le crash. Le reste de la console reste traduisible :
 * dans les aéroports, un passager étranger doit pouvoir traduire sa manette.
 */
export function AnimatorLayout() {
    useHeadTags(ANIMATOR_HEAD_TAGS);

    // `translate="no"` + `.notranslate` sur <html> : les deux marqueurs que Chrome
    // respecte pour ne pas traduire une page. Retirés en quittant la zone pour que
    // le reste de la console (manette des passagers) reste traduisible.
    useEffect(() => {
        const html = document.documentElement;
        html.setAttribute('translate', 'no');
        html.classList.add('notranslate');
        return () => {
            html.removeAttribute('translate');
            html.classList.remove('notranslate');
        };
    }, []);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;
        navigator.serviceWorker.register('/animator-sw.js', { scope: '/animator' }).catch(() => {
            /* http local ou navigateur sans SW : la page marche pareil, sans hors-ligne. */
        });
    }, []);

    return <Outlet />;
}

/**
 * Bandeau « Installer l'app » affiché en haut de la liste des établissements.
 * - Android / Chrome desktop : bouton qui déclenche le prompt natif d'installation.
 * - iOS : pas de prompt programmable (limite Apple) → instructions Partager →
 *   « Sur l'écran d'accueil ».
 * - Navigateur sans installation (Firefox Android…) : rien, plutôt qu'un bouton mort.
 */
export function AnimatorInstallBanner() {
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useLocalStorage('animatorPwaInstallDismissed', false);
    const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(() => deferredPrompt);

    useEffect(() => {
        // Rattrape une émission arrivée après l'init du state (le listener module
        // tourne d'abord et a déjà fait le preventDefault).
        const onPrompt = (e: Event) => setPromptEvent(e as BeforeInstallPromptEvent);
        window.addEventListener('beforeinstallprompt', onPrompt);
        return () => window.removeEventListener('beforeinstallprompt', onPrompt);
    }, []);

    if (dismissed || isStandalone()) return null;
    if (!promptEvent && !IS_IOS) return null;

    const install = async () => {
        const evt = promptEvent;
        if (!evt) return;
        // Consommé tout de suite : un 2e clic pendant le dialogue natif ne doit
        // pas rappeler prompt() sur le même événement (InvalidStateError).
        setPromptEvent(null);
        deferredPrompt = null;
        evt.prompt();
        const { outcome } = await evt.userChoice;
        if (outcome === 'accepted') setDismissed(true);
    };

    return (
        <div className={styles.installBanner}>
            <img className={styles.installIcon} src="/assets/images/animator-icon-192.png" alt="" />
            <div className={styles.installText}>
                <strong>{t('animator.install.title')}</strong>
                {promptEvent ? (
                    <span>{t('animator.install.subtitle')}</span>
                ) : (
                    <span>
                        <Trans
                            i18nKey="animator.install.iosHint"
                            components={[
                                <LuShare key="share" className={styles.installInlineIcon} />,
                                <LuSquarePlus key="add" className={styles.installInlineIcon} />,
                            ]}
                        />
                    </span>
                )}
            </div>
            {promptEvent && (
                <button type="button" className={styles.installBtn} onClick={install}>
                    {t('animator.install.button')}
                </button>
            )}
            <button
                type="button"
                className={styles.installClose}
                onClick={() => setDismissed(true)}
                aria-label={t('animator.install.close')}
            >
                <LuX />
            </button>
        </div>
    );
}
